import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { ArrowLeft, Video, Play, ClipboardCheck, FileText, MessageSquare, Library } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

// Import existing management components
import TeacherClasses from "./TeacherClasses";
import TeacherLiveClasses from "./TeacherLiveClasses";
import TeacherExams from "./TeacherExams";
import TeacherAssignments from "./TeacherAssignments";
import DoubtPortal from "./DoubtPortal";

export default function TeacherCourseHub() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [activeTab, setActiveTab] = useState("recorded");

  useEffect(() => {
    // Fetch course details
    api.get("/courses").then(res => {
      const c = res.data.find(c => c.id === Number(courseId));
      if (c) setCourse(c);
      else toast.error("Course not found");
    }).catch(() => toast.error("Failed to load course details"));
  }, [courseId]);

  if (!course) return <div className="p-10 text-center animate-pulse">Loading Course Hub...</div>;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b dark:border-slate-800 px-6 py-4 flex items-center gap-4 sticky top-0 z-40">
        <button onClick={() => navigate("/teacher-dashboard")} className="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-rose-600 mb-1">Teacher Course Console</div>
          <h1 className="text-xl font-black text-slate-900 dark:text-white truncate max-w-2xl">{course.title}</h1>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white dark:bg-slate-900 border-b dark:border-slate-800 px-6 overflow-x-auto no-scrollbar">
        <div className="flex gap-8 min-w-max">
          {[
            { id: "recorded", label: "Recorded Classes", icon: Play },
            { id: "live", label: "Live Classes", icon: Video },
            { id: "exams", label: "Mock Tests", icon: ClipboardCheck },
            { id: "assignments", label: "Assignments", icon: FileText },
            { id: "library", label: "Digital Library", icon: Library },
            { id: "doubts", label: "Doubt Desk", icon: MessageSquare },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? "border-rose-600 text-rose-600" 
                  : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
         {/* Since the components usually assume global or check URL, we might need to pass courseId as prop if they support it, 
             or wrap them in a context. We will pass courseId prop if we can modify them later, or just render them and let them use the global state if they filter on their own.
             Actually, we should modify these components to accept courseId prop or read from useParams(). */}
         
         <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden">
            {activeTab === "recorded" && <TeacherClasses defaultCourseId={courseId} isEmbedded={true} />}
            {activeTab === "live" && <TeacherLiveClasses defaultCourseId={courseId} isEmbedded={true} />}
            {activeTab === "exams" && <TeacherExams defaultCourseId={courseId} isEmbedded={true} />}
            {activeTab === "assignments" && <TeacherAssignments defaultCourseId={courseId} isEmbedded={true} />}
            {activeTab === "doubts" && <DoubtPortal defaultCourseId={courseId} isEmbedded={true} />}
            {activeTab === "library" && <div className="p-10 text-center text-slate-500 font-bold">Manage Books logic coming soon for Course Hub...</div>}
         </div>
      </div>
    </div>
  );
}
